import { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import AssetMaintenance from "models/AssetMaintenance"
import Form from "components/Form/Form"
import CustomError from "models/CustomError"
import { IApiLink, IValidationRule } from "@/types"
import ValidationService from "services/ValidationService"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import FormField from "components/FormField/FormField"
import useMaintenanceTypes from "hooks/useMaintenanceTypes"
import Select from "components/Select/Select"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import { Grid, GridItem } from "components/Grid"
import InputDateTime from "components/InputDateTime/InputDateTime"
import useInputNumber from "components/InputNumber/hooks/useInputNumber"
import InputNumber from "components/InputNumber/InputNumber"

interface IMaintenanceEditModalProps {
    maintenance?: AssetMaintenance
    linkCreateAssetMaintenance?: IApiLink
    onClose: () => void
    onChanged: () => Promise<void>
}

const MaintenanceEditModal: FC<IMaintenanceEditModalProps> = ({
    maintenance,
    linkCreateAssetMaintenance,
    onClose,
    onChanged,
}) => {
    const { t } = useTranslation()
    const isEdit = !!maintenance
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [typeId, setTypeId] = useState(maintenance?.typeId || null)
    const [thresholdDate, setThresholdDate] = useState(maintenance?.thresholdDate || null)
    const [thresholdOperationHours, thresholdOperationHoursNumber, setThresholdOperationHours] = useInputNumber(maintenance?.thresholdOperationHours || "")

    const isThresholdDateRequired = !thresholdOperationHoursNumber
    const isThresholdOperationHoursRequired = !thresholdDate

    const { maintenanceTypeSelectOptions, fetchMaintenanceTypes } = useMaintenanceTypes()

    useEffect(() => {
        if (maintenanceTypeSelectOptions) return
        fetchMaintenanceTypes?.()
    }, [fetchMaintenanceTypes, maintenanceTypeSelectOptions])

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        const validationFields: [string, IValidationRule, any][] = [
            ["typeId", { required: true }, typeId],
            ["thresholdDate", { required: isThresholdDateRequired }, thresholdDate],
            ["thresholdOperationHours", { required: isThresholdOperationHoursRequired, min: 0, type: "integer" }, thresholdOperationHours],
        ]
        return ValidationService.validateMultiple(validationFields)
    }, [typeId, isThresholdDateRequired, thresholdDate, isThresholdOperationHoursRequired, thresholdOperationHours])

    async function handleSave() {
        setIsSubmitted(true)
        const link = maintenance
            ? maintenance.linkUpdate
            : linkCreateAssetMaintenance

        if (Object.keys(validationErrors).length > 0
            || !typeId
            || (!thresholdDate && !thresholdOperationHoursNumber)
            || !link) {
            return
        }

        setIsSubmitting(true)

        try {
            await AssetService.createUpdateAssetMaintenance(link, {
                typeId,
                thresholdDate,
                thresholdOperationHours: thresholdOperationHoursNumber || null,
            })
            await onChanged()
            onClose()
        } catch(e) {
            toastMessages.showSystemError()
            setIsSubmitting(false)
        }
    }

    return (
        <ConfirmModal
            title={isEdit
                ? t('maintenance.changeMaintenance')
                : t('maintenance.scheduleNewMaintenance')
            }
            maxWidth="708px"
            onClose={onClose}
            onConfirm={handleSave}
            confirmButtonLabel={isEdit
                ? t('common.save')
                : t('maintenance.scheduleNewMaintenance')
            }
            isBusy={isSubmitting}>
            <Form onSubmit={handleSave} className="py-1">
                <FormField
                    label={t('maintenance.maintenanceType')}>
                    <Select
                        value={typeId}
                        options={maintenanceTypeSelectOptions}
                        onChange={o => setTypeId(o?.value || null)}
                        isInvalid={isSubmitted && !!validationErrors.typeId}
                        isDisabled={isSubmitting || !maintenanceTypeSelectOptions}
                        placeholder={maintenanceTypeSelectOptions ? undefined : t('common.loading')}
                        isClearable
                        isInModal
                    />
                    {isSubmitted && !!validationErrors.typeId &&
                        <ValidationMessage>{validationErrors.typeId.message}</ValidationMessage>
                    }
                </FormField>
                <div className="mt-2 mb-1">
                    {t('maintenance.thresholdDescr')}
                </div>
                <Grid columns="1fr 1fr" gap="2.4rem">
                    <GridItem>
                        <FormField
                            label={t('maintenance.thresholdDate')}
                            optional={!isThresholdDateRequired}>
                            <InputDateTime
                                type="date"
                                date={thresholdDate}
                                utc
                                onDateChange={setThresholdDate}
                                isInvalid={isSubmitted && !!validationErrors.thresholdDate}
                                disabled={isSubmitting}
                                clearButton
                            />
                            {isSubmitted && !!validationErrors.thresholdDate &&
                                <ValidationMessage>{validationErrors.thresholdDate.message}</ValidationMessage>
                            }
                        </FormField>
                    </GridItem>
                    <GridItem>
                        <FormField
                            label={t('maintenance.thresholdOperatingHours')}
                            optional={!isThresholdOperationHoursRequired}>
                            <InputNumber
                                value={thresholdOperationHours}
                                onChange={setThresholdOperationHours}
                                isSubmitted={isSubmitted}
                                isInvalid={!!validationErrors.thresholdOperationHours}
                                disabled={isSubmitting}
                                after={<div className="px-2">{t('common.hours')}</div>}
                            />
                            {isSubmitted && !!validationErrors.thresholdOperationHours &&
                                <ValidationMessage>{validationErrors.thresholdOperationHours.message}</ValidationMessage>
                            }
                        </FormField>
                    </GridItem>
                </Grid>
            </Form>
        </ConfirmModal>
    )
}

export default MaintenanceEditModal
